import { Button, Typography } from "@cur8/maneki";
import { useCreateNekoLensAuthUrl } from "hooks/queries/useCreateNekoLensAuthUrl";
import { CheckInParams } from "lib/types/checkInParameters";
import QRCode from "qrcode";
import { useEffect, useRef } from "react";
import { InformationLayout } from "render/ui/layout/InformationLayout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import styles from "./styles.module.sass";

type NekoLensAuthViewProps = {
  onBack(): void;
} & CheckInParams;
// Pre-signed URL is valid for 5 minutes, this value needs to be less than that
const AUTO_HIDE_DELAY = 240000;

export function NekoLensAuthView({
  onBack,
  patientId,
  visitId,
}: NekoLensAuthViewProps) {
  const createNekoLensAuthUrlMutation = useCreateNekoLensAuthUrl();
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onBack();
    }, AUTO_HIDE_DELAY);

    return () => {
      clearTimeout(timeout);
    };
  }, [onBack]);

  useEffect(() => {
    const url = createNekoLensAuthUrlMutation.data;
    const img = imgRef.current;
    if (!url || !img) {
      return;
    }
    const value = constructQrBytes(patientId, visitId, url);
    QRCode.toDataURL([{ data: value, mode: "byte" }], {
      errorCorrectionLevel: "L",
    }).then((it) => {
      img.src = it;
    });
  }, [createNekoLensAuthUrlMutation.data, patientId, visitId]);

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <div className={styles.frame}>
              <img ref={imgRef} className={styles.img} />
            </div>

            <div className={styles.instructions}>
              <Typography variant="title-s" color="subtle">
                Scan the QR code with NekoLens to assign the member to the
                device
              </Typography>
            </div>
          </section>
        }
        buttons={
          <Button variant="secondary" fillContainer onPress={onBack}>
            Close
          </Button>
        }
      />
    </PageFrameContent>
  );
}

export const constructQrBytes = (
  patientId: string,
  visitId: string,
  url: string
) => {
  const parts = url.split("=p1");
  const prefix = parts[0] + "=p1";
  const base64 = parts[1]!.slice(0, parts[1]!.length);

  const encoder = new TextEncoder();
  const idBytes = encoder.encode(patientId);
  const visitBytes = encoder.encode(visitId);
  const prefixBytes = encoder.encode(prefix);

  // For unpadded base64: calculate exact decoded length
  const fullBlocks = Math.floor(base64.length / 4);
  const remainder = base64.length % 4;
  const decodedLength = fullBlocks * 3 + (remainder > 0 ? remainder - 1 : 0);
  const bytes = new Uint8Array(
    idBytes.length + visitBytes.length + prefixBytes.length + decodedLength + 4
  );

  let i = 0;
  bytes[i++] = 0; // marker byte
  bytes.set(idBytes, i);
  i += idBytes.length;
  bytes[i++] = 0;
  bytes.set(visitBytes, i);
  i += visitBytes.length;
  bytes[i++] = 0;
  bytes.set(prefixBytes, i);
  i += prefixBytes.length;
  bytes[i++] = 0;
  urlSafeAtob(base64, bytes.subarray(i));

  return bytes;
};

const urlSafeAtob = (b64: string, bytes: Uint8Array) => {
  let i = 0;
  let j = 0;

  // Process complete 4-character blocks
  const fullBlocks = Math.floor(b64.length / 4);
  for (let block = 0; block < fullBlocks; block++, i += 4, j += 3) {
    const c0 = decode(b64.charCodeAt(i));
    const c1 = decode(b64.charCodeAt(i + 1));
    const c2 = decode(b64.charCodeAt(i + 2));
    const c3 = decode(b64.charCodeAt(i + 3));

    bytes[j] = (c0 << 2) | (c1 >> 4);
    bytes[j + 1] = ((c1 & 15) << 4) | (c2 >> 2);
    bytes[j + 2] = ((c2 & 3) << 6) | c3;
  }

  // Handle remaining 2 or 3 characters (unpadded base64)
  const remaining = b64.length % 4;
  if (remaining === 2) {
    const c0 = decode(b64.charCodeAt(i));
    const c1 = decode(b64.charCodeAt(i + 1));
    bytes[j] = (c0 << 2) | (c1 >> 4);
  } else if (remaining === 3) {
    const c0 = decode(b64.charCodeAt(i));
    const c1 = decode(b64.charCodeAt(i + 1));
    const c2 = decode(b64.charCodeAt(i + 2));
    bytes[j] = (c0 << 2) | (c1 >> 4);
    bytes[j + 1] = ((c1 & 15) << 4) | (c2 >> 2);
  }

  return bytes;
};

const decode = (b64: number) => {
  if (b64 >= 65 && b64 <= 90) {
    // A-Z
    return b64 - 65;
  }
  if (b64 >= 97 && b64 <= 122) {
    // a-z
    return b64 - 97 + 26;
  }
  if (b64 >= 48 && b64 <= 57) {
    // 0-9
    return b64 - 48 + 52;
  }
  if (b64 === 45) {
    // -
    return 62;
  }
  if (b64 === 95) {
    // _
    return 63;
  }
  return 0;
};
